import { useRef, useState } from "react";

import { GripVertical } from "lucide-react";

import { cn } from "@/utils/ui";

type ResizablePanelsProps = {
  left: React.ReactNode;
  right?: React.ReactNode | null;
  className?: string;
  /**
   * Which panel the drag handle sizes. Default `"right"` keeps the original
   * behaviour (DocumentSorter): the right panel is sized, left fills the rest.
   * `"left"` sizes the left panel (Assessment Scribe sections list) and lets
   * the right panel fill the rest. The handle always sits on the sized
   * panel's inner edge.
   */
  sizedSide?: "left" | "right";
  /**
   * Sizing unit. Default `"percent"` (right-side, container-relative %).
   * `"px"` sizes the panel in pixels within a fixed clamp range.
   */
  unit?: "percent" | "px";
  // Percent mode (sizedSide="right", unit="percent")
  defaultRightWidthPercent?: number;
  minRightWidthPercent?: number;
  maxRightWidthPercent?: number;
  // Pixel mode (unit="px") — currently used with sizedSide="left"
  defaultLeftWidth?: number;
  minLeftWidth?: number;
  maxLeftWidth?: number;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const ResizablePanels = ({
  left,
  right,
  className,
  sizedSide = "right",
  unit = "percent",
  defaultRightWidthPercent = 33,
  minRightWidthPercent = 20,
  maxRightWidthPercent = 65,
  defaultLeftWidth = 288,
  minLeftWidth = 200,
  maxLeftWidth = 420,
}: ResizablePanelsProps) => {
  const isPx = unit === "px";
  const isLeft = sizedSide === "left";

  // Width state is interpreted per `unit`: percent of container (right mode)
  // or pixels (px mode). One state covers both so the handler stays simple.
  const [size, setSize] = useState(
    isPx ? defaultLeftWidth : defaultRightWidthPercent
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);

    if (!containerRef.current) return;
    const startX = e.clientX;
    const startSize = size;

    if (isPx) {
      const onPointerMove = (ev: PointerEvent) => {
        // Handle on the left panel's right edge → dragging right widens it.
        const next = startSize + (ev.clientX - startX);
        setSize(clamp(next, minLeftWidth, maxLeftWidth));
      };
      const onPointerUp = () => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
      };
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
      return;
    }

    // Percent mode: capture layout once — avoids repeated reflows on every
    // pointermove. Right panel sized as a % of the container.
    const { left: containerLeft, width } =
      containerRef.current.getBoundingClientRect();

    const onPointerMove = (ev: PointerEvent) => {
      const newRightWidth = ((containerLeft + width - ev.clientX) / width) * 100;
      setSize(clamp(newRightWidth, minRightWidthPercent, maxRightWidthPercent));
    };

    const onPointerUp = () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };

  const handle = (
    <div
      data-testid="resize-handle"
      className="group relative flex w-4 flex-shrink-0 cursor-col-resize select-none items-center justify-center transition-colors hover:bg-primary/5 active:bg-primary/10"
      onPointerDown={handlePointerDown}
      title="Drag to resize"
    >
      <GripVertical className="text-muted-foreground/40 h-4 w-4 transition-colors group-hover:text-primary group-active:text-primary" />
    </div>
  );

  // Left-sized px mode: sized left panel + handle on its right edge, right
  // content fills the rest.
  if (isLeft) {
    return (
      <div ref={containerRef} className={cn("flex h-full flex-row", className)}>
        <div
          className="flex min-h-0 min-w-0 flex-shrink-0"
          style={{ width: `${size}px` }}
        >
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            {left}
          </div>
          {right != null && handle}
        </div>
        {right != null && (
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            {right}
          </div>
        )}
      </div>
    );
  }

  // Default right-sized percent mode (unchanged behaviour).
  return (
    <div ref={containerRef} className={cn("flex h-full flex-row", className)}>
      {/* Left panel — fills remaining space */}
      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">{left}</div>

      {/* Right panel + drag handle — only rendered when right content is provided */}
      {right != null && (
        <div
          className="flex min-h-0 min-w-0 flex-shrink-0"
          style={{ width: `${size}%` }}
        >
          {handle}
          {/* Right content */}
          <div className="min-h-0 min-w-0 flex-1 overflow-hidden">{right}</div>
        </div>
      )}
    </div>
  );
};
